import { type Stripe_InvoiceID } from '@imago/api-client-stripe'
import { type TF_FormID } from '@imago/api-client-typeform'
import { type YCBM_IntentSecret } from '@imago/api-client-ycbm'
import { type EMRInfoJSON, type EMRTypeID, type EncounterStatus, type FileID, type UserID } from './documents/_EMR.ts'
import { type CareInfoJSON } from './documents/Care.ts'
import { type GAHTTreatmentFeedbackContent } from './documents/GAHTTreatmentFeedback.ts'
import { type OnboardingConsentAcceptance } from './documents/LegalConsent.ts'
import { type ProductID, type ServiceID } from './documents/ServiceProduct.ts'
import { type UserServicesRequestContentJSON } from './documents/UserServicesRequest.ts'
import { type UserSurgeryRequestContentJSON } from './documents/UserSurgeryRequest.ts'

export type IntentRequest = BuyIntentRequest | PayIntentRequest | BookIntentRequest | FileIntentRequest

export interface BuyIntentRequest {
	verb: 'buy'
	productID: ProductID
	// Present when buying GAHT onboarding: the legal documents the user accepted
	// at the payment step. Required server-side for the onboarding purchase.
	consent?: OnboardingConsentAcceptance
}

export interface PayIntentRequest {
	verb: 'pay'
	invoiceID: Stripe_InvoiceID
}

export interface BookIntentRequest {
	verb: 'book'
	serviceID: ServiceID
}

export interface FileIntentRequest {
	verb: 'file'
	formID: EMRTypeID
}

export type Intent = StripeIntent | YCBMIntent | TFIntent

export interface StripeIntent {
	stripe_secret: StripeIntentSecret
}

export interface StripeIntentSecret {
	amountToPay: number
	amountNote?: string
	publishableKey?: string
	clientSecret: string
	customerSessionClientSecret: string | undefined
}

export interface YCBMIntent {
	ycbm_secret: YCBMIntentSecret
}

export type YCBMIntentSecret = YCBM_IntentSecret

export interface TFIntent {
	tf_secret_v2: TFIntentSecretV2
}

export interface TFIntentSecretV2 {
	formID: TF_FormID
	hidden: Record<string, string>
}

export type IntentRequestToIntent<TIntentRequest extends IntentRequest> =
	TIntentRequest extends BuyIntentRequest ? StripeIntent :
	TIntentRequest extends PayIntentRequest ? StripeIntent :
	TIntentRequest extends BookIntentRequest ? YCBMIntent :
	TIntentRequest extends FileIntentRequest ? TFIntent :
	never

export interface Imago_GoogleWorkspace_Event_XProperties_Private_Patch {
	emrsFolderID?: UserID | null
	emrID?: FileID | null
	status?: EncounterStatus | null
}

export interface Imago_GoogleWorkspace_Event_XProperties_Patch {
	private?: Imago_GoogleWorkspace_Event_XProperties_Private_Patch
}

export interface Imago_GoogleWorkspace_Event_XProperties_Private extends Imago_GoogleWorkspace_Event_XProperties_Private_Patch {
	emrsFolderID?: UserID
	emrID?: FileID
	status?: EncounterStatus
}

export interface Imago_GoogleWorkspace_Event_XProperties extends Imago_GoogleWorkspace_Event_XProperties_Patch {
	private?: Imago_GoogleWorkspace_Event_XProperties_Private
	shared?: {}
}

export interface API_Submit_Request {
	gahtTreatmentFeedback?: GAHTTreatmentFeedbackContent,
	userServicesRequest?: UserServicesRequestContentJSON,
	userSurgeryRequest?: UserSurgeryRequestContentJSON,
}

export type API_Submit_Response = EMRInfoJSON[]

export interface API_Care_Subscribe_Response {
	care: CareInfoJSON
	intent: StripeIntent | null
}

export interface API_Care_Terminate_Response {
	care: CareInfoJSON
}
